/**
 * Admin Users Page
 *
 * Lists all users with search, filtering, pagination, and role management.
 */

import {
  Alert,
  AlertDescription,
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute } from '@tanstack/react-router';
import {
  CheckCircle,
  Clock,
  Mail,
  Pencil,
  Search,
  Shield,
  Trash2,
  User,
  UserPlus,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '@/hooks/use-api';
import {
  DEFAULT_PAGINATION,
  formatDate,
  type PaginationInfo,
} from '@/lib/admin-utils';

export const Route = createFileRoute('/_app/admin/users/')({
  component: AdminUsersPage,
});

interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface UserRecord {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: 'user' | 'admin';
  organization_id: string | null;
  organizations: Organization | null;
  created_at: string;
  updated_at: string;
  // Auth metadata
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  confirmed_at: string | null;
  invited_at: string | null;
}

function AdminUsersPage() {
  const getApi = useApi();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [pagination, setPagination] =
    useState<PaginationInfo>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<UserRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<'user' | 'admin'>('user');
  const [newUserOrgId, setNewUserOrgId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Organizations for dropdown
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();
      const query: Record<string, string> = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      };
      if (searchDebounce) {
        query.search = searchDebounce;
      }
      if (roleFilter !== 'all') {
        query.role = roleFilter;
      }

      const response = await api.admin.users.$get({ query });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const json = await response.json();
      if ('data' in json) {
        const data = json.data as unknown as {
          items: UserRecord[];
          pagination: PaginationInfo;
        };
        setUsers(data.items);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchDebounce, roleFilter, getApi]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch organizations for dropdown
  const fetchOrganizations = useCallback(async () => {
    try {
      const api = await getApi();
      const response = await api.admin.organizations.$get({
        query: { limit: '100' },
      });
      if (response.ok) {
        const json = await response.json();
        if ('data' in json) {
          const data = json.data as unknown as { items: Organization[] };
          setOrganizations(data.items);
        }
      }
    } catch {
      // Silently fail - organizations dropdown will be empty
    }
  }, [getApi]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Create/invite user
  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);

    try {
      const api = await getApi();
      const response = await api.admin.users.$post({
        json: {
          email: newUserEmail,
          ...(newUserName && { fullName: newUserName }),
          role: newUserRole,
          organizationId: newUserOrgId || null,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          ('error' in data && typeof data.error === 'string'
            ? data.error
            : null) || 'Failed to invite user'
        );
      }

      // Reset form and close modal
      setNewUserEmail('');
      setNewUserName('');
      setNewUserRole('user');
      setNewUserOrgId('');
      setCreateModalOpen(false);

      // Refresh list
      fetchUsers();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to invite user'
      );
    } finally {
      setCreating(false);
    }
  };

  // Open edit modal
  const handleEdit = (user: UserRecord) => {
    setSelectedUser(user);
    setEditRole(user.role);
    setSaveError(null);
    setEditModalOpen(true);
  };

  // Save user changes
  const handleSave = async () => {
    if (!selectedUser) return;

    setSaving(true);
    setSaveError(null);

    try {
      const api = await getApi();
      const response = await api.admin.users.$patch({
        json: {
          id: selectedUser.id,
          role: editRole,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          ('error' in data && typeof data.error === 'string'
            ? data.error
            : null) || 'Failed to update user'
        );
      }

      // Close modal and refresh
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save changes'
      );
    } finally {
      setSaving(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (user: UserRecord) => {
    setDeletingUser(user);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  // Delete user
  const handleDelete = async () => {
    if (!deletingUser) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const api = await getApi();
      const response = await api.admin.users[':userId'].$delete({
        param: { userId: deletingUser.id },
      });

      if (!response.ok && response.status !== 204) {
        const data = await response.json();
        throw new Error(
          ('error' in data && typeof data.error === 'string'
            ? data.error
            : null) || 'Failed to delete user'
        );
      }

      setDeleteModalOpen(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete user'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Format relative time (e.g., "2 hours ago", "3 days ago")
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Get user status
  const getUserStatus = (user: UserRecord) => {
    if (user.email_confirmed_at || user.confirmed_at) {
      return { status: 'active', label: 'Active', variant: 'success' as const };
    }
    if (user.invited_at) {
      return {
        status: 'pending',
        label: 'Pending',
        variant: 'warning' as const,
      };
    }
    return {
      status: 'inactive',
      label: 'Inactive',
      variant: 'default' as const,
    };
  };

  // Table columns
  const columns = [
    {
      key: 'email',
      header: 'User',
      sortable: true,
      render: (user: UserRecord) => (
        <Inline gap="sm">
          <Avatar
            size="sm"
            {...(user.avatar_url ? { src: user.avatar_url } : {})}
            fallback={user.full_name || user.email}
          />
          <Stack gap="none">
            <Text weight="medium">{user.full_name || 'No name'}</Text>
            <Text color="muted" size="xs">
              {user.email}
            </Text>
          </Stack>
        </Inline>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: UserRecord) => (
        <Badge variant={user.role === 'admin' ? 'warning' : 'default'}>
          {user.role === 'admin' ? (
            <Text as="span" className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Admin
            </Text>
          ) : (
            <Text as="span" className="flex items-center gap-1">
              <User className="h-3 w-3" />
              User
            </Text>
          )}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: UserRecord) => {
        const { status, label, variant } = getUserStatus(user);
        return (
          <Badge variant={variant}>
            <Text as="span" className="flex items-center gap-1">
              {status === 'active' && <CheckCircle className="h-3 w-3" />}
              {status === 'pending' && <Mail className="h-3 w-3" />}
              {status === 'inactive' && <Clock className="h-3 w-3" />}
              {label}
            </Text>
          </Badge>
        );
      },
    },
    {
      key: 'last_sign_in',
      header: 'Last Login',
      render: (user: UserRecord) => {
        const lastLogin = formatRelativeTime(user.last_sign_in_at);
        return (
          <Text size="sm" color={lastLogin ? 'default' : 'muted'}>
            {lastLogin || 'Never'}
          </Text>
        );
      },
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (user: UserRecord) => (
        <Text size="sm" color={user.organizations ? 'default' : 'muted'}>
          {user.organizations?.name || 'No organization'}
        </Text>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      sortable: true,
      render: (user: UserRecord) => (
        <Text size="sm" color="muted">
          {formatDate(user.created_at)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (user: UserRecord) => (
        <Inline gap="xs" justify="end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(user)}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Edit
            </Text>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteModal(user)}
            className="text-destructive hover:text-destructive h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
            <Text as="span" className="sr-only">
              Delete
            </Text>
          </Button>
        </Inline>
      ),
    },
  ];

  return (
    <Stack gap="lg" className="p-6">
      {/* Header */}
      <PageHeader
        title="Users"
        subtitle="Manage users and their roles"
        actions={
          <Button onClick={() => setCreateModalOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        }
      />

      {/* Filters */}
      <Inline gap="md" wrap>
        <Stack className="min-w-[200px] flex-1">
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </Stack>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
      </Inline>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchUsers}
              className="mt-2"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        emptyMessage="No users found"
        pagination={{
          page: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
        }}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
      />

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditModalOpen(false);
            setSelectedUser(null);
            setSaveError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              {selectedUser ? `Editing ${selectedUser.email}` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {selectedUser && (
              <Stack gap="md">
                {saveError && (
                  <Alert variant="destructive">
                    <AlertDescription>{saveError}</AlertDescription>
                  </Alert>
                )}

                {/* User info */}
                <Card className="bg-surface-2">
                  <CardContent className="flex items-center gap-3 p-4">
                    <Avatar
                      size="lg"
                      {...(selectedUser.avatar_url
                        ? { src: selectedUser.avatar_url }
                        : {})}
                      fallback={selectedUser.full_name || selectedUser.email}
                    />
                    <Stack gap="none">
                      <Text weight="medium">
                        {selectedUser.full_name || 'No name'}
                      </Text>
                      <Text color="muted" size="sm">
                        {selectedUser.email}
                      </Text>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Role selection */}
                <Stack gap="xs">
                  <Label>Role</Label>
                  <Select
                    value={editRole}
                    onValueChange={(v) => setEditRole(v as 'user' | 'admin')}
                  >
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">
                        <Text as="span" className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          User
                        </Text>
                      </SelectItem>
                      <SelectItem value="admin">
                        <Text as="span" className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Admin
                        </Text>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Text color="muted" size="xs" className="mt-1">
                    Admins have full access to the admin panel and all
                    organizations.
                  </Text>
                </Stack>

                {/* Organization info */}
                <Stack gap="xs">
                  <Label>Organization</Label>
                  <Text color="secondary" size="sm" className="mt-1">
                    {selectedUser.organizations?.name ||
                      'No organization assigned'}
                  </Text>
                </Stack>
              </Stack>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedUser(null);
                setSaveError(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteModalOpen(false);
            setDeletingUser(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {deleteError && (
                <Alert variant="destructive">
                  <AlertDescription>{deleteError}</AlertDescription>
                </Alert>
              )}
              {deletingUser && (
                <Card className="bg-surface-2">
                  <CardContent className="p-4">
                    <Inline gap="sm">
                      <Avatar
                        size="default"
                        {...(deletingUser.avatar_url
                          ? { src: deletingUser.avatar_url }
                          : {})}
                        fallback={deletingUser.full_name || deletingUser.email}
                      />
                      <Stack gap="none">
                        <Text weight="medium">
                          {deletingUser.full_name || 'No name'}
                        </Text>
                        <Text color="muted" size="sm">
                          {deletingUser.email}
                        </Text>
                      </Stack>
                    </Inline>
                  </CardContent>
                </Card>
              )}
              <Text color="muted" size="sm">
                The user will lose access to all organizations and projects.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingUser(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Invite Modal */}
      <Dialog
        open={createModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateModalOpen(false);
            setNewUserEmail('');
            setNewUserName('');
            setNewUserRole('user');
            setNewUserOrgId('');
            setCreateError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation email to a new user.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="md">
              {createError && (
                <Alert variant="destructive">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}
              <Stack gap="xs">
                <Label htmlFor="userEmail">
                  Email Address{' '}
                  <Text as="span" color="error">
                    *
                  </Text>
                </Label>
                <Input
                  id="userEmail"
                  type="email"
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="mt-1"
                />
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="userName">Full Name</Label>
                <Input
                  id="userName"
                  type="text"
                  placeholder="John Doe"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  className="mt-1"
                />
                <Text color="muted" size="xs" className="mt-1">
                  Optional. The user can set their name when they accept the
                  invitation.
                </Text>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="userRole">Role</Label>
                <Select
                  value={newUserRole}
                  onValueChange={(v) => setNewUserRole(v as 'user' | 'admin')}
                >
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">
                      <Text as="span" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        User
                      </Text>
                    </SelectItem>
                    <SelectItem value="admin">
                      <Text as="span" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin
                      </Text>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="userOrg">Organization</Label>
                <Select
                  value={newUserOrgId || 'none'}
                  onValueChange={(v) => setNewUserOrgId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No organization</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Text color="muted" size="xs" className="mt-1">
                  Optional. Assign the user to an organization.
                </Text>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateModalOpen(false);
                setNewUserEmail('');
                setNewUserName('');
                setNewUserRole('user');
                setNewUserOrgId('');
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newUserEmail}
              loading={creating}
            >
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
